import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { INetworkServiceProvider } from '@/types/api-types'
import StatusCodeType from '@/enum/StatusCodeType'
import useGlobalStore from '@/stores/modules/global'
import ACCOUNT from '@/api/account/endpoints'
import { ListPagingTypeExpression } from '@/enum/LinqExpressionType'
import ErrorHandlerService from './ErrorHandlerService'

class AxiosServiceProvider implements INetworkServiceProvider {
  service: AxiosInstance
  handlingExplicitly?: boolean

  constructor(config: AxiosRequestConfig, unabortable?: boolean, handlingExplicitly?: boolean) {
    this.service = axios.create(config)
    this.handlingExplicitly = handlingExplicitly
    if (!unabortable) {
      this.setupRequestInterceptors()
    }
    this.setupResponseInterceptors()
  }

  private setupRequestInterceptors() {
    this.service.interceptors.request.use(
      (config) => {
        const controller = AxiosServiceProvider.getAbortController(config.url as string)
        return {
          ...config,
          signal: controller?.signal
        }
      },
      (error) => {
        ErrorHandlerService.handleNetworkError(error)
        return Promise.reject(error)
      }
    )
  }

  private setupResponseInterceptors() {
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.status === StatusCodeType.Success) {
          AxiosServiceProvider.removeAbortController(response.config.url as string)
        }
        return response
      },
      (error) => {
        const errorRequiresExplicitHandling =
          [ACCOUNT.REQUEST(), ACCOUNT.RESET_PASSWORD()].includes(error.config.url) ||
          error.config.url.includes(ListPagingTypeExpression.Offset) ||
          error.response?.status === StatusCodeType.Unauthorized
        if (errorRequiresExplicitHandling || this.handlingExplicitly) return Promise.reject(error)
        ErrorHandlerService.handleNetworkError(error)
        return Promise.resolve(error)
      }
    )
  }

  static getAbortController(controllerRef: string) {
    const globalStore = useGlobalStore()
    return globalStore.addAbortController(controllerRef, true)
  }

  static removeAbortController(controllerRef: string) {
    const globalStore = useGlobalStore()
    globalStore.removeExistingAbortController(controllerRef, true)
  }

  async sendGetRequest(path: string): Promise<AxiosResponse> {
    return this.service
      .get(path)
      .then((response) => response)
      .catch((error) => {
        throw error
      })
  }

  async sendPostRequest(path: string, data: object, noAuth?: boolean): Promise<AxiosResponse> {
    if (noAuth) {
      const { Authorization, ...rest } = this.service.defaults.headers
      this.service.defaults.headers = rest
    }
    return this.service
      .post(path, data)
      .then((response) => response)
      .catch((error) => {
        throw error
      })
  }

  async sendPutRequest(path: string, data: object): Promise<AxiosResponse> {
    return this.service
      .put(path, data)
      .then((response) => response)
      .catch((error) => {
        throw error
      })
  }

  async sendDeleteRequest(path: string): Promise<AxiosResponse> {
    return this.service
      .delete(path)
      .then((response) => response)
      .catch((error) => {
        throw error
      })
  }
}
export default AxiosServiceProvider
